<template>
  <div class="delivery-operations-container glass-theme">
    <!-- API Loading Overlay with Animation -->
    <transition name="loading-overlay">
      <div v-if="apiLoading" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-map-marked-alt"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Header with filters -->
    <div class="operations-header">
      <div class="header-title">
        <h2>🚚 Delivery Operations Dashboard</h2>
        <p>View and manage home delivery trips and orders</p>
      </div>
      <div class="header-filters">
        <div class="date-filter">
          <label>📅 Select Date</label>
          <div class="calendar-picker-container">
            <input 
              type="text" 
              class="date-input calendar-input"
              :class="{ 'calendar-active': showCalendarModal }"
              :value="formattedSelectedDate"
              placeholder="Select date"
              autocomplete="off"
              readonly
              @click="openCalendarModal"
            />
            <i class="calendar-icon fas fa-calendar-alt" @click="openCalendarModal"></i>
          </div>
        </div>
        <div v-if="packet.userDetails && packet.userDetails.userrole === 'admin'" class="location-filter">
          <label>📍 Location</label>
          <select v-model="locationId" @change="onLocationChange" class="location-select">
            <option :value="90">🌍 ALL LOCATIONS</option>
            <option v-for="loc in locationOptions" :key="loc.id" :value="loc.id">
              {{ loc.name }}
            </option>
          </select>
        </div>
        <button @click="fetchTrips" :disabled="apiLoading" class="refresh-btn">
          <span v-if="apiLoading" class="spinner-sm"></span>
          <span v-else>🔄</span> Refresh
        </button>
      </div>
    </div>

    <!-- Main content with trips sidebar and map -->
    <div class="operations-content">
      <!-- Trips Sidebar -->
      <div class="trips-sidebar" :class="{ 'collapsed': sidebarCollapsed }">
    <div class="sidebar-header">
  <h3 v-if="!sidebarCollapsed">📋 Trips ({{ trips.length }})</h3>
  <button 
    @click="toggleSidebar" 
    class="collapse-btn"
    :title="sidebarCollapsed ? 'Expand trips panel' : 'Collapse trips panel'"
  >
    {{ sidebarCollapsed ? '▶' : '◀' }}
  </button>
</div>
        
        <div v-if="!sidebarCollapsed" class="trips-list">
          <div v-if="tripsLoading" class="loading-trips">
            <div class="spinner"></div>
            <p>Loading trips...</p>
          </div>
          
          <div v-else-if="trips.length === 0" class="no-trips">
            <p>No trips found for {{ formatDate(selectedDate) }}</p>
          </div>
          
          <div v-else class="trip-items">
            <div 
              v-for="trip in trips" 
              :key="trip.id"
              class="trip-card"
              :class="{ 'active': selectedTrip && selectedTrip.id === trip.id }"
              @click="selectTrip(trip)"
            >
              <div class="trip-gradient-bg"></div>
              <div class="trip-header">
                <div class="trip-id">
                  <span class="trip-icon">🚚</span>
                  <strong>{{ trip.trip_no || trip.trip_id || trip.id }}</strong>
                </div>
                <!-- <div class="trip-badge" :class="getTripStatusClass(trip.status)">
                  {{ trip.status || 'Active' }}
                </div> -->
              </div>
              
              <div class="trip-details">
                <div class="detail-row">
                  <div class="detail-item">
                    <span class="detail-icon">📦</span>
                    <div class="detail-info">
                      <span class="detail-label">Cubes:</span>
                      <span class="detail-value">{{ trip.cubes || 0 }}</span>
                    </div>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">⏱️</span>
                    <div class="detail-info">
                      <span class="detail-label">Assembling Time:</span>
                      <span class="detail-value">{{ trip.assembling_time || 0 }} min</span>
                    </div>
                  </div>
                </div>
                <div class="detail-row">
                  <div class="detail-item">
                    <span class="detail-icon">🎯</span>
                    <div class="detail-info">
                      <span class="detail-label">Deliveries:</span>
                      <span class="detail-value highlight">{{ trip.deliveries_completed || 0 }} / {{ trip.total_deliveries || 0 }}</span>
                    </div>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">📦</span>
                    <div class="detail-info">
                      <span class="detail-label">Items:</span>
                      <span class="detail-value highlight">{{ trip.ready_to_ship_items || 0 }} / {{ trip.total_items || 0 }}</span>
                    </div>
                  </div>
                </div>
                <div class="detail-row grid-3">
                  <div class="detail-item">
                    <span class="detail-icon">🚛</span>
                    <div class="detail-info2">
                      <span class="detail-label">Truck</span>
                      <span class="detail-value truck-name">{{ trip.truck_name || 'N/A' }}</span>
                    </div>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">👨‍✈️</span>
                    <div class="detail-info2">
                      <span class="detail-label">Driver</span>
                      <span class="detail-value">{{ trip.driver_name || 'Not assigned' }}</span>
                    </div>
                  </div>
                  <div class="detail-item">
                    <span class="detail-icon">🏢</span>
                    <div class="detail-info2">
                      <span class="detail-label">Contractor</span>
                      <span class="detail-value">{{ trip.contractor_name || 'N/A' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Container -->
      <div class="map-container" ref="mapContainer">
        <div v-if="mapLoading" class="map-loading">
          <div class="spinner"></div>
          <p>Loading map...</p>
        </div>
        <div v-else-if="mapError" class="map-error">
          <p>❌ Failed to load map</p>
          <button @click="initMap" class="retry-btn">Retry</button>
        </div>
        <div 
          id="deliveryMap" 
          class="delivery-map"
          :class="{ 'map-zooming': mapZooming }"
        ></div>
        
        <!-- Map Legend -->
        <div class="map-legend" v-if="!sidebarCollapsed">
          <div class="legend-title">📍 Status Legend</div>
          <div class="legend-item">
            <span class="legend-color" style="background: linear-gradient(135deg, #10b981, #34d399);"></span>
            <span>Delivered</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: linear-gradient(135deg, #f59e0b, #fbbf24);"></span>
            <span>In Progress</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: linear-gradient(135deg, #3b82f6, #60a5fa);"></span>
            <span>Pending</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: linear-gradient(135deg, #8b5cf6, #a78bfa);"></span>
            <span>Scheduled</span>
          </div>
          <div class="legend-item">
            <span class="legend-color" style="background: linear-gradient(135deg, #ef4444, #f87171);"></span>
            <span>Cancelled</span>
          </div>
          <div class="legend-divider"></div>
          <div class="legend-item">
            <span class="legend-line" style="background: linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6);"></span>
            <span>Route Path</span>
          </div>
        </div>
        
        <!-- Order details panel -->
        <transition name="panel-slide">
          <div v-if="selectedOrder" class="order-details-panel" @click.stop>
            <div class="panel-header">
              <h4>📦 Order Details</h4>
              <button @click="selectedOrder = null" class="close-panel">×</button>
            </div>
            <div class="panel-content">
              <div class="detail-group">
                <label>Order No:</label>
                <span>{{ selectedOrder.order_no || selectedOrder.id }}</span>
              </div>
              <div class="detail-group">
                <label>Customer:</label>
                <span>{{ selectedOrder.ship_to_name || selectedOrder.customer_name }}</span>
              </div>
              <div class="detail-group">
                <label>Address:</label>
                <span>{{ selectedOrder.ship_to_address || selectedOrder.address }}</span>
              </div>
              <div class="detail-group">
                <label>City:</label>
                <span>{{ selectedOrder.ship_to_city || selectedOrder.city }}</span>
              </div>
              <div class="detail-group">
                <label>State:</label>
                <span>{{ selectedOrder.ship_to_state || selectedOrder.state }}</span>
              </div>
              <div class="detail-group">
                <label>Zip:</label>
                <span>{{ selectedOrder.ship_to_zipcode || selectedOrder.ship_to_zip || selectedOrder.zip }}</span>
              </div>
              <div class="detail-group">
                <label>Phone:</label>
                <span>{{ selectedOrder.ship_to_phone || selectedOrder.phone }}</span>
              </div>
              <div class="detail-group">
                <label>Status:</label>
                <span class="status-badge" :class="getOrderStatusClass(selectedOrder.status)">
                  {{ selectedOrder.status || 'Pending' }}
                </span>
              </div>
              <div class="detail-group" v-if="selectedOrder.requested_delivery_date">
                <label>Requested Date:</label>
                <span>{{ selectedOrder.requested_delivery_date }}</span>
              </div>
              <div class="detail-group" v-if="selectedOrder.schedule_date">
                <label>Schedule Date:</label>
                <span>{{ selectedOrder.schedule_date }}</span>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Trip Calendar Modal -->
    <div v-if="showCalendarModal" class="calendar-modal-overlay" @click.self="closeCalendarModal">
      <div class="calendar-modal-content">
        <div class="calendar-modal-header">
          <h3>📅 Select Delivery Date</h3>
          <button @click="closeCalendarModal" class="modal-close-btn">×</button>
        </div>
        
        <div class="calendar-modal-body">
          <!-- Month Navigation -->
          <div class="calendar-nav">
            <button @click="prevMonth" class="nav-btn">
              <i class="fas fa-chevron-left"></i> Prev
            </button>
            <span class="current-month">{{ currentMonthName }} {{ currentYear }}</span>
            <button @click="nextMonth" class="nav-btn">
              Next <i class="fas fa-chevron-right"></i>
            </button>
            <div class="calendar-location-badge" v-if="locationId !== 90">
              📍 {{ locationName || 'Filtered' }}
            </div>
          </div>

          <!-- Loading state for calendar -->
          <div v-if="calendarLoading" class="calendar-loading">
            <div class="spinner-sm"></div>
            <span>Loading trip data...</span>
          </div>

          <!-- Calendar Grid -->
          <div v-else class="calendar-grid">
            <!-- Weekday headers -->
            <div class="calendar-weekday" v-for="day in weekdays" :key="day">
              {{ day }}
            </div>
            
            <!-- Calendar days - Updated with detailed stats -->
            <div 
              v-for="(day, index) in calendarDays" 
              :key="index"
              class="calendar-day"
              :class="{
                'other-month': !day.isCurrentMonth,
                'today': day.isToday,
                'has-deliveries': day.deliveryCount > 0,
                'selected': day.date === selectedDate
              }"
              @click="selectDateFromCalendar(day.date)"
            >
              <div class="day-number">{{ day.day }}</div>
              
              <!-- Detailed stats like reference code -->
              <div v-if="day.deliveryCount > 0" class="day-stats">
                <div class="stat-row">🚚 Trips: {{ day.tripCount || 0 }}</div>
                <div class="stat-row">📦 Scheduled: {{ day.deliveryCount || 0 }}</div>
                <div class="stat-row">⚠️ Un-Assigned: {{ day.unassigned || 0 }}</div>
                <div class="stat-row">📍 Invalid: {{ day.invalidAddresses || 0 }}</div>
              </div>
              
              <div v-else class="no-deliveries">
                <span>—</span>
              </div>
            </div>
          </div>
        </div>

        <div class="calendar-modal-footer">
          <button @click="closeCalendarModal" class="cancel-btn">Cancel</button>
          <button @click="goToToday" class="today-btn">Today</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Requestor from '@/utilities/Requestor';
import moment from 'moment';

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

// Colorful marker icons
const createColoredMarker = (color, number, status) => {
  let shapeHtml = '';
  
  if (status === 'delivered' || status === 'completed') {
    shapeHtml = `<div style="
      background: linear-gradient(135deg, ${color}, #34d399);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 4px 15px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    ">
      ✓
    </div>`;
  } else if (status === 'in_progress') {
    shapeHtml = `<div style="
      background: linear-gradient(135deg, ${color}, #fbbf24);
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: 3px solid white;
      box-shadow: 0 4px 15px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 14px;
      cursor: pointer;
      transform: rotate(45deg);
      transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    ">
      <span style="transform: rotate(-45deg);">${number}</span>
    </div>`;
  } else if (status === 'cancelled' || status === 'failed') {
    shapeHtml = `<div style="
      background: linear-gradient(135deg, ${color}, #f87171);
      width: 32px;
      height: 32px;
      border-radius: 4px;
      border: 3px solid white;
      box-shadow: 0 4px 15px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 18px;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    ">
      ✗
    </div>`;
  } else {
    shapeHtml = `<div style="
      background: linear-gradient(135deg, ${color}, #60a5fa);
      width: 34px;
      height: 34px;
      border-radius: 50% 50% 50% 0;
      border: 3px solid white;
      box-shadow: 0 4px 15px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 14px;
      cursor: pointer;
      transform: rotate(-45deg);
      transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    ">
      <span style="transform: rotate(45deg);">${number}</span>
    </div>`;
  }
  
  return L.divIcon({
    html: shapeHtml,
    className: `order-marker marker-${status}`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
  });
};

export default {
  name: 'DeliveryOperations',
  
  data() {
    return {
      packet: {},
      apiLoading: false,
      tripsLoading: false,
      mapLoading: false,
      mapZooming: false,
      mapError: false,
      sidebarCollapsed: false,
      selectedDate: new Date().toISOString().split('T')[0],
      locationId: 90, // Default to ALL
      locationOptions: [],
      trips: [],
      selectedTrip: null,
      selectedOrder: null,
      map: null,
      markers: [],
      routeLayers: [],
      arrowMarkers: [],
      geocodeCache: new Map(),
      locationName: '',
      isAdmin: false, // Track if user is admin
      
      // Loading animation state
      loadingMessage: 'Loading Dashboard',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,
      resizeObserver: null,
      
      // Calendar Modal State
      showCalendarModal: false,
      calendarLoading: false,
      currentViewDate: moment(),
      calendarData: {},
      
      weekdays: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
      
      tips: [
        "🗺️ Loading map data...",
        "📍 Fetching delivery locations...",
        "🚚 Retrieving trip information...",
        "📦 Processing orders...",
        "🎯 Optimizing route visualization...",
        "🔄 Syncing with server...",
        "✨ Almost ready!",
        "📊 Preparing delivery dashboard..."
      ]
    };
  },
  
  computed: {
    formattedSelectedDate() {
      if (!this.selectedDate) return 'Select date';
      return moment(this.selectedDate).format('MMM D, YYYY');
    },
    
    currentYear() {
      return this.currentViewDate.year();
    },
    
    currentMonthName() {
      return this.currentViewDate.format('MMMM');
    },
    
    calendarDays() {
      const year = this.currentViewDate.year();
      const month = this.currentViewDate.month();
      
      const firstDayOfMonth = moment([year, month, 1]);
      const startDayOfWeek = firstDayOfMonth.day();
      
      const startDate = firstDayOfMonth.clone().subtract(startDayOfWeek, 'days');
      const days = [];
      const today = moment().startOf('day');
      
      for (let i = 0; i < 42; i++) {
        const currentDate = startDate.clone().add(i, 'days');
        const dateStr = currentDate.format('YYYY-MM-DD');
        const tripInfo = this.calendarData[dateStr] || { 
          tripCount: 0, 
          deliveryCount: 0,
          unassigned: 0,
          invalidAddresses: 0
        };
        
        days.push({
          date: dateStr,
          day: currentDate.date(),
          isCurrentMonth: currentDate.month() === month,
          isToday: currentDate.isSame(today, 'day'),
          tripCount: tripInfo.tripCount || 0,
          deliveryCount: tripInfo.deliveryCount || 0,
          unassigned: tripInfo.unassigned || 0,
          invalidAddresses: tripInfo.invalidAddresses || 0
        });
      }
      
      return days;
    }
  },
  
  mounted() {
    this.loadUser();
    this.initMap();
    this.fetchTrips();
    this.setupResizeObserver();
  },
  
  beforeDestroy() {
    this.cleanupMap();
    this.stopLoadingAnimation();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.body.style.overflow = '';
  },
  
  methods: {
    // ========== Calendar Modal Methods ==========
    
    openCalendarModal() {
      this.showCalendarModal = true;
      document.body.style.overflow = 'hidden';
      this.fetchCalendarData();
    },
    
    closeCalendarModal() {
      this.showCalendarModal = false;
      document.body.style.overflow = '';
    },
    
    goToToday() {
      this.currentViewDate = moment();
      this.fetchCalendarData();
    },
    
    prevMonth() {
      this.currentViewDate = this.currentViewDate.clone().subtract(1, 'month');
      this.fetchCalendarData();
    },
    
    nextMonth() {
      this.currentViewDate = this.currentViewDate.clone().add(1, 'month');
      this.fetchCalendarData();
    },
    
    async fetchCalendarData() {
      this.calendarLoading = true;
      
      const year = this.currentViewDate.year();
      const month = this.currentViewDate.month() + 1;
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      
      try {
        let url = `/api/sales/envio_delivery_counts?company_id=90&month=${monthStr}&type=home_delivery`;
        
        // For non-admin users, always send location_id and location_name from user details
        if (!this.isAdmin && this.locationId && this.locationId !== 90) {
          url += `&location_id=${this.locationId}`;
          // Always send location_name when location is selected
          const locationNameToSend = this.locationName || (this.packet.locationsdict && this.packet.locationsdict[this.locationId]) || '';
          if (locationNameToSend) {
            url += `&location_name=${encodeURIComponent(locationNameToSend)}`;
          }
        } 
        // For admin users, only send location if they selected a specific one
        else if (this.isAdmin && this.locationId && this.locationId !== 90) {
          url += `&location_id=${this.locationId}`;
          const locationNameToSend = this.locationName || (this.packet.locationsdict && this.packet.locationsdict[this.locationId]) || '';
          if (locationNameToSend) {
            url += `&location_name=${encodeURIComponent(locationNameToSend)}`;
          }
        }
        
        const response = await Requestor.getAsync(url);
        
        let resultArray = null;
        
        if (response.data && response.data.data && response.data.data.data && response.data.data.data.result) {
          resultArray = response.data.data.data.result;
        } else if (response.data && response.data.data && response.data.data.result) {
          resultArray = response.data.data.result;
        } else if (response.data && response.data.result) {
          resultArray = response.data.result;
        } else if (response.result) {
          resultArray = response.result;
        } else if (response.data && Array.isArray(response.data)) {
          resultArray = response.data;
        }
        
        const newCalendarData = {};
        
        if (resultArray && Array.isArray(resultArray)) {
          for (const item of resultArray) {
            if (item.date) {
              newCalendarData[item.date] = {
                tripCount: item.trips_by_date || 0,
                deliveryCount: item.deliveries_by_date || 0,
                unassigned: item.unassigned_orders_by_date || 0,
                invalidAddresses: item.invalid_address_orders || 0
              };
            }
          }
        }
        
        this.calendarData = newCalendarData;
        
      } catch (err) {
        console.error('Error fetching calendar data:', err);
      } finally {
        this.calendarLoading = false;
      }
    },
    
    selectDateFromCalendar(date) {
      this.selectedDate = date;
      this.fetchTrips();
      this.closeCalendarModal();
    },
    
    onLocationChange() {
      if (this.locationId !== 90 && this.packet.locationsdict && this.packet.locationsdict[this.locationId]) {
        this.locationName = this.packet.locationsdict[this.locationId];
      } else {
        this.locationName = '';
      }
      // Use nextTick to ensure locationName is updated before API calls
      this.$nextTick(() => {
        this.fetchTrips();
        if (this.showCalendarModal) {
          this.fetchCalendarData();
        }
      });
    },
    
    // ========== Setup Methods ==========
    
    setupResizeObserver() {
      const mapContainer = this.$refs.mapContainer;
      if (mapContainer && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.invalidateMapSize();
        });
        this.resizeObserver.observe(mapContainer);
      }
    },
    
    invalidateMapSize() {
      if (this.map && this.map.invalidateSize) {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
      }
    },
    
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      setTimeout(() => {
        this.invalidateMapSize();
      }, 350);
    },
    
    // ========== API Loading Methods ==========
    
    startApiLoading(message = 'Loading data') {
      this.apiCallStack++;
      if (!this.apiLoading) {
        this.startLoadingAnimation(message);
      } else {
        this.loadingMessage = message;
      }
    },
    
    stopApiLoading() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0 && this.apiLoading) {
        this.stopLoadingAnimation(true);
      }
    },
    
    startLoadingAnimation(message = 'Loading Dashboard') {
      this.apiLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
      
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++;
        }
      }, 2000);
      
      let messages = ['Loading Dashboard', 'Fetching Data', 'Processing Information', 'Updating Map', 'Finalizing'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        }
      }, 1800);
    },
    
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      if (this.msgInterval) clearInterval(this.msgInterval);
      
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => {
          this.apiLoading = false;
        }, 500);
      } else {
        this.apiLoading = false;
      }
    },
    
    // ========== User Data Methods ==========
    
    loadUser() {
      try {
        const packetData = localStorage.packet || '{}';
        const userDetailsData = localStorage.userDetails || '{}';
        
        this.packet = JSON.parse(packetData);
        this.packet.userDetails = JSON.parse(userDetailsData);
        
        // Check if user is admin
        this.isAdmin = this.packet.userDetails && 
                       this.packet.userDetails.userrole && 
                       this.packet.userDetails.userrole.toLowerCase() === 'admin';
        
        // CRITICAL CHANGE: For non-admin users, get location ID from packet user details
        if (!this.isAdmin) {
          // Get location ID from packet user details
          const userLocationId = this.packet.userDetails.location_id || 
                                 this.packet.userDetails.locationId || 
                                 this.packet.userDetails.LocationID;
          
          if (userLocationId) {
            this.locationId = parseInt(userLocationId);
            
            // Get location name from locationsdict if available
            if (this.packet.locationsdict && this.packet.locationsdict[this.locationId]) {
              this.locationName = this.packet.locationsdict[this.locationId];
            } else {
              this.locationName = this.packet.userDetails.location_name || 
                                 this.packet.userDetails.locationName || 
                                 '';
            }
          } else {
            console.warn('No location ID found in user details for non-admin user');
            this.showToast('Location information not found. Please contact administrator.', 'error');
          }
        }
        
        // Populate location options for admin users (or if needed)
        if (this.packet.locationsdict) {
          this.locationOptions = Object.entries(this.packet.locationsdict)
            .filter(([id, name]) => {
              const excluded = ["OUT OF STOCK", "101 WH", "9TH ST ", "ONLINE SALES", "ALL", "FOLCROFT"];
              return !excluded.includes(name.toUpperCase());
            })
            .map(([id, name]) => ({
              id: parseInt(id),
              name: name.toUpperCase()
            }));
        }
        
      } catch (err) {
        console.error('Error loading user:', err);
        this.packet = { userDetails: {} };
        this.isAdmin = false;
      }
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    
    // ========== Trip Methods ==========
    
    async fetchTrips() {
      if (!this.selectedDate) return;
      
      this.tripsLoading = true;
      this.startApiLoading('Loading trips data...');
      this.selectedTrip = null;
      this.clearMarkers();
      
      try {
        const payload = {
          company_id: 90,
          created_at: this.selectedDate,
          trip_type: 'home_delivery'
        };
        
        // CRITICAL: For non-admin users, always send location_id and location_name
        if (!this.isAdmin && this.locationId && this.locationId !== 90) {
          payload.location_id = this.locationId;
          if (this.locationName) {
            payload.location_name = this.locationName;
          }
        } 
        // For admin users, send location only if they selected something other than ALL
        else if (this.isAdmin && this.locationId && this.locationId !== 90) {
          payload.location_id = this.locationId;
          if (this.locationName) {
            payload.location_name = this.locationName;
          }
        }
        
        const response = await Requestor.postAsync('/api/sales/envio_trips_by_date', payload);
        
        let tripsData = null;
        
        if (response.data && response.data.result && response.data.result.trips) {
          tripsData = response.data.result.trips;
        } else if (response.data && response.data.result && Array.isArray(response.data.result)) {
          tripsData = response.data.result;
        } else if (response.result && response.result.trips) {
          tripsData = response.result.trips;
        } else if (response.result && Array.isArray(response.result)) {
          tripsData = response.result;
        } else if (response.data && response.data.trips) {
          tripsData = response.data.trips;
        } else if (Array.isArray(response.data)) {
          tripsData = response.data;
        }
        
        if (tripsData && Array.isArray(tripsData) && tripsData.length > 0) {
          this.trips = tripsData.map(trip => ({
            id: trip.id,
            trip_id: trip.trip_no || trip.id,
            trip_no: trip.trip_no,
            driver_name: trip.driver.name || 'Not assigned',
            driver: trip.driver.name,
            contractor_name: trip.contractor.name || 'N/A',
            ready_to_ship_items: trip.ready_to_shipped_quantity || 0,
            total_items: trip.total_items || 0,
            contractor: trip.contractor.name,
            assembling_time: trip.selected_assembly_time || 'NA',
            truck_name: trip.truck.truck_name || trip.truck_name || 'N/A',
            order_count: trip.orders_count || (trip.orders ? trip.orders.length : 0),
            orders_count: trip.orders_count,
            deliveries_completed: trip.orders ? trip.orders.filter(o => o.status === 'delivered' || o.status === 'completed').length : 0,
            total_deliveries: trip.orders_count || 0,
            cubes: trip.assigned_cubes || 0,
            status: trip.status,
            orders: trip.orders || [],
            order_ids: trip.orders ? trip.orders.map(o => o.order_no || o.id) : [],
            schedule_date: trip.schedule_date,
            trip_start_time: trip.trip_start_time,
            trip_end_time: trip.trip_end_time
          }));
          
          if (this.trips.length > 0 && !this.selectedTrip) {
            await this.selectTrip(this.trips[0]);
          }
        } else {
          this.trips = [];
          this.showToast(`No trips found for ${this.formatDate(this.selectedDate)}`, 'info');
        }
        
      } catch (err) {
        console.error('Error fetching trips:', err);
        this.showToast(err.message || 'Failed to load trips. Please try again.', 'error');
        this.trips = [];
      } finally {
        this.tripsLoading = false;
        this.stopApiLoading();
      }
    },
    
    async selectTrip(trip) {
      this.selectedTrip = trip;
      this.selectedOrder = null;
      this.clearMarkers();
      
      if (trip.orders && trip.orders.length > 0) {
        this.plotOrdersOnMap(trip.orders, trip);
        return;
      }
      
      this.startApiLoading('Loading order data for trip...');
      
      try {
        let url = `/api/sales/envio_orders_by_date?company_id=90&date=${this.selectedDate}&type=home_delivery`;
        
        // CRITICAL: For non-admin users, always send location_name
        if (!this.isAdmin && this.locationName) {
          url += `&location_name=${encodeURIComponent(this.locationName)}`;
        } 
        // For admin users, send location_name if they selected a specific location
        else if (this.isAdmin && this.locationId !== 90 && this.locationName) {
          url += `&location_name=${encodeURIComponent(this.locationName)}`;
        }
        
        const response = await Requestor.getAsync(url);
        
        let ordersData = null;
        
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          ordersData = response.data.data;
        } else if (response.data && response.data.result && Array.isArray(response.data.result)) {
          ordersData = response.data.result;
        } else if (response.data && Array.isArray(response.data)) {
          ordersData = response.data;
        } else if (Array.isArray(response)) {
          ordersData = response;
        } else if (response.result && Array.isArray(response.result)) {
          ordersData = response.result;
        }
        
        if (ordersData && ordersData.length > 0) {
          ordersData = ordersData.filter(order => order.trip_id === trip.id);
          trip.orders = ordersData;
          this.plotOrdersOnMap(ordersData, trip);
        } else {
          this.showToast('No orders found for this trip', 'info');
        }
        
      } catch (err) {
        console.error('Error fetching orders:', err);
        this.showToast('Failed to load order details', 'error');
      } finally {
        this.stopApiLoading();
      }
    },
    
    // ========== Map & Geocoding Methods ==========
    
    async geocodeAddress(order) {
      const address = order.ship_to_address || '';
      const city = order.ship_to_city || '';
      const state = order.ship_to_state || '';
      const zip = order.ship_to_zipcode || order.ship_to_zip || '';
      
      const addressParts = [address, city, state, zip].filter(part => part && part.trim());
      const fullAddress = addressParts.join(', ');
      
      if (!fullAddress) return null;
      
      if (this.geocodeCache.has(fullAddress)) {
        return this.geocodeCache.get(fullAddress);
      }
      
      try {
        const encodedAddress = encodeURIComponent(fullAddress);
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`);
        const data = await response.json();
        
        if (data && data.length > 0) {
          const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
          this.geocodeCache.set(fullAddress, coords);
          return coords;
        }
        
        if (order.order_lat && order.order_lng) {
          const coords = { lat: parseFloat(order.order_lat), lng: parseFloat(order.order_lon || order.order_lng) };
          this.geocodeCache.set(fullAddress, coords);
          return coords;
        }
        
        return null;
      } catch (err) {
        console.error('Geocoding error:', err);
        return null;
      }
    },
    
    async plotOrdersOnMap(orders, trip) {
      if (!this.map || !this.mapInitialized()) return;
      
      this.mapLoading = true;
      
      const addressMap = new Map();
      const validOrders = [];
      
      for (const order of orders) {
        const addressKey = `${order.ship_to_address}|${order.ship_to_city}|${order.ship_to_state}`;
        if (!addressMap.has(addressKey)) {
          addressMap.set(addressKey, order);
          validOrders.push(order);
        }
      }
      
      const orderLocations = [];
      
      for (const order of validOrders) {
        const coords = await this.geocodeAddress(order);
        if (coords) {
          orderLocations.push({ ...order, lat: coords.lat, lng: coords.lng });
        }
      }
      
      if (orderLocations.length === 0) {
        this.mapLoading = false;
        this.showToast('No valid addresses found for geocoding', 'warning');
        return;
      }
      
      this.clearMarkers();
      
      const bounds = L.latLngBounds([]);
      
      orderLocations.forEach((order, index) => {
        const markerColor = this.getOrderMarkerColor(order.status);
        const customIcon = createColoredMarker(markerColor, index + 1, order.status);
        
        const marker = L.marker([order.lat, order.lng], { icon: customIcon })
          .addTo(this.map)
          .bindPopup(this.createOrderPopup(order));
        
        marker.on('click', () => {
          this.selectedOrder = order;
        });
        
        this.markers.push(marker);
        bounds.extend([order.lat, order.lng]);
      });
      
      if (orderLocations.length > 1) {
        this.drawColorfulRoute(orderLocations);
      }
      
      this.animateMapZoom(bounds);
      
      setTimeout(() => {
        this.mapLoading = false;
      }, 300);
    },
    
    animateMapZoom(bounds) {
      if (!this.map) return;
      
      this.mapZooming = true;
      this.invalidateMapSize();
      
      setTimeout(() => {
        if (bounds.isValid() && this.markers.length > 0) {
          if (this.markers.length === 1) {
            this.map.setView(this.markers[0].getLatLng(), 13, {
              animate: true,
              duration: 0.6,
              easeLinearity: 0.25
            });
          } else {
            this.map.fitBounds(bounds, {
              padding: [50, 50],
              animate: true,
              duration: 0.6,
              easeLinearity: 0.25
            });
          }
        }
        
        setTimeout(() => {
          this.mapZooming = false;
        }, 700);
      }, 50);
    },
    
    drawColorfulRoute(orders) {
      this.clearRoute();
      
      const latlngs = orders.map(order => [order.lat, order.lng]);
      const colors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];
      
      for (let i = 0; i < latlngs.length - 1; i++) {
        const colorIndex = i % colors.length;
        const segmentLayer = L.polyline([latlngs[i], latlngs[i + 1]], {
          color: colors[colorIndex],
          weight: 5,
          opacity: 0.9,
          lineJoin: 'round',
          lineCap: 'round'
        }).addTo(this.map);
        
        this.routeLayers.push(segmentLayer);
      }
      
      this.addAnimatedArrows(latlngs);
    },
    
    addAnimatedArrows(latlngs) {
      if (!this.map || latlngs.length < 2) return;
      
      for (let i = 0; i < latlngs.length - 1; i++) {
        const from = latlngs[i];
        const to = latlngs[i + 1];
        const angle = Math.atan2(to[0] - from[0], to[1] - from[1]) * 180 / Math.PI;
        
        for (let t = 0.2; t <= 0.8; t += 0.3) {
          const midLat = from[0] + (to[0] - from[0]) * t;
          const midLng = from[1] + (to[1] - from[1]) * t;
          
          const arrowIcon = L.divIcon({
            html: `<div style="
              transform: rotate(${angle}deg);
              font-size: 18px;
              color: #ffffff;
              text-shadow: 0 0 3px rgba(0,0,0,0.5);
              filter: drop-shadow(0 2px 2px rgba(0,0,0,0.3));
            ">▶</div>`,
            className: 'route-arrow',
            iconSize: [16, 16],
            iconAnchor: [8, 8]
          });
          
          const arrowMarker = L.marker([midLat, midLng], { icon: arrowIcon }).addTo(this.map);
          this.arrowMarkers.push(arrowMarker);
        }
      }
    },
    
    createOrderPopup(order) {
      const statusColor = this.getOrderMarkerColor(order.status);
      return `
        <div class="order-popup" style="border-left: 4px solid ${statusColor};">
          <div class="popup-header">
            <strong>${order.order_no || order.id}</strong>
            <span class="popup-status" style="background: ${statusColor}20; color: ${statusColor};">
              ${order.status || 'Pending'}
            </span>
          </div>
          <div class="popup-body">
            <div><b>👤 Customer:</b> ${order.ship_to_name || order.customer_name || 'N/A'}</div>
            <div><b>📍 Address:</b> ${order.ship_to_address || order.address || 'N/A'}</div>
            <div><b>🏙️ City:</b> ${order.ship_to_city || order.city || 'N/A'}</div>
            <div><b>📞 Phone:</b> ${order.ship_to_phone || order.phone || 'N/A'}</div>
          </div>
          <div class="popup-footer">
            <button class="view-order-btn" onclick="event.stopPropagation()">View Details</button>
          </div>
        </div>
      `;
    },
    
    getOrderMarkerColor(status) {
      const statusColors = {
        'delivered': '#10b981',
        'completed': '#10b981',
        'in_progress': '#f59e0b',
        'pending': '#3b82f6',
        'scheduled': '#8b5cf6',
        'cancelled': '#ef4444',
        'failed': '#ef4444'
      };
      return statusColors[status.toLowerCase()] || '#6b7280';
    },
    
    getOrderStatusClass(status) {
      if (!status) return 'status-pending';
      const statusMap = {
        'delivered': 'status-delivered',
        'completed': 'status-delivered',
        'in_progress': 'status-progress',
        'scheduled': 'status-scheduled',
        'pending': 'status-pending',
        'cancelled': 'status-cancelled'
      };
      return statusMap[status.toLowerCase()] || 'status-pending';
    },
    
    getTripStatusClass(status) {
      if (!status) return 'badge-active';
      const statusMap = {
        'active': 'badge-active',
        'started': 'badge-active',
        'completed': 'badge-completed',
        'cancelled': 'badge-cancelled'
      };
      return statusMap[status.toLowerCase()] || 'badge-active';
    },
    
    initMap() {
      this.mapError = false;
      this.mapLoading = true;
      this.startApiLoading('Initializing map...');
      
      setTimeout(() => {
        const mapElement = document.getElementById('deliveryMap');
        
        if (!mapElement) {
          setTimeout(() => this.initMap(), 300);
          return;
        }
        
        if (this.map) {
          this.cleanupMap();
        }
        
        try {
          this.map = L.map('deliveryMap').setView([39.8283, -98.5795], 4);
          
          L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CartoDB',
            subdomains: 'abcd',
            maxZoom: 19,
            minZoom: 3
          }).addTo(this.map);
          
          L.control.scale({ metric: true, imperial: true }).addTo(this.map);
          
          this.map.on('load', () => {
            this.mapLoading = false;
            this.stopApiLoading();
          });
          
          setTimeout(() => {
            if (this.map) {
              this.invalidateMapSize();
              this.mapLoading = false;
              this.stopApiLoading();
            }
          }, 200);
          
        } catch (err) {
          console.error('Error initializing map:', err);
          this.mapError = true;
          this.mapLoading = false;
          this.stopApiLoading();
        }
      }, 150);
    },
    
    mapInitialized() {
      return this.map && !this.mapError && !this.mapLoading;
    },
    
    clearRoute() {
      if (this.routeLayers.length) {
        this.routeLayers.forEach(layer => {
          if (layer && layer.remove) layer.remove();
        });
        this.routeLayers = [];
      }
      
      if (this.arrowMarkers.length) {
        this.arrowMarkers.forEach(marker => {
          if (marker && marker.remove) marker.remove();
        });
        this.arrowMarkers = [];
      }
    },
    
    clearMarkers() {
      if (this.markers.length) {
        this.markers.forEach(marker => {
          if (marker && marker.remove) marker.remove();
        });
        this.markers = [];
      }
      this.clearRoute();
    },
    
    cleanupMap() {
      this.clearMarkers();
      if (this.map) {
        try {
          this.map.remove();
        } catch (err) {
          console.warn('Error removing map:', err);
        }
        this.map = null;
      }
      this.stopLoadingAnimation();
    },
    
    showToast(message, variant = 'info') {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: variant === 'error' ? 'Error' : variant === 'success' ? 'Success' : 'Info',
          variant: variant,
          autoHideDelay: 3000,
          solid: true
        });
      } else {
        console.log(`${variant}: ${message}`);
      }
    }
  },
  
  watch: {
    locationId(newVal, oldVal) {
      if (newVal !== 90 && this.packet.locationsdict && this.packet.locationsdict[newVal]) {
        this.locationName = this.packet.locationsdict[newVal];
      } else {
        this.locationName = '';
      }
      // Use nextTick to ensure locationName is updated before API call
      this.$nextTick(() => {
        this.fetchTrips();
        if (this.showCalendarModal) {
          this.fetchCalendarData();
        }
      });
    }
  }
};
</script>

<style scoped>
/* Glass Theme Background */
.delivery-operations-container.glass-theme {
  position: relative;
  background: transparent !important;
  overflow: hidden;
}

/* Background image with blur effect */
.delivery-operations-container.glass-theme::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(10px);
  opacity: 0.5;
  z-index: -2;
  transform: scale(1.1);
}

.delivery-operations-container {
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Glass Theme Header */
.delivery-operations-container.glass-theme .operations-header {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

/* Glass Theme Sidebar */
.delivery-operations-container.glass-theme .trips-sidebar {
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.3);
}

/* Glass Theme Trip Cards */
.delivery-operations-container.glass-theme .trip-card {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.delivery-operations-container.glass-theme .trip-card:hover {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.delivery-operations-container.glass-theme .trip-card.active {
  background: linear-gradient(135deg, rgba(57, 114, 207, 0.85), rgba(54, 64, 80, 0.9));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
}

/* Glass Theme Order Details Panel */
.delivery-operations-container.glass-theme .order-details-panel {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
}

/* Glass Theme Map Legend */
.delivery-operations-container.glass-theme .map-legend {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
}

/* Glass Theme Calendar Modal */
.delivery-operations-container.glass-theme .calendar-modal-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.delivery-operations-container.glass-theme .calendar-day {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(5px);
  transition: all 0.2s ease;
}

.delivery-operations-container.glass-theme .calendar-day:hover {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  transform: scale(0.98);
}

.delivery-operations-container.glass-theme .calendar-day.selected {
  background: rgba(0, 142, 213, 0.15);
  border: 2px solid rgba(0, 142, 213, 0.6);
}

/* Glass Theme Loading Card */
.delivery-operations-container.glass-theme .loading-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
}

/* Glass Theme Form Inputs */
.delivery-operations-container.glass-theme .date-input,
.delivery-operations-container.glass-theme .location-select {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.2s ease;
}

.delivery-operations-container.glass-theme .date-input:focus,
.delivery-operations-container.glass-theme .location-select:focus {
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(10px);
  border-color: rgba(54, 64, 80, 0.5);
  outline: none;
}

/* Glass Theme Sidebar Header */
.delivery-operations-container.glass-theme .sidebar-header {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);
}

/* Glass Theme Refresh Button */
.delivery-operations-container.glass-theme .refresh-btn {
  background: linear-gradient(135deg, rgba(54, 64, 80, 0.9), rgba(54, 64, 80, 0.95));
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.delivery-operations-container.glass-theme .refresh-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(54, 64, 80, 1), rgba(54, 64, 80, 1));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Glass Theme Calendar Navigation */
.delivery-operations-container.glass-theme .calendar-nav {
  background: rgba(248, 250, 252, 0.9);
  backdrop-filter: blur(10px);
}

.delivery-operations-container.glass-theme .nav-btn {
  background: rgba(241, 245, 249, 0.9);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Glass Theme Modal Footer */
.delivery-operations-container.glass-theme .calendar-modal-footer {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(226, 232, 240, 0.5);
}

/* Header Styles */
.operations-header {
  background: rgba(255,255,255,0.95);
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255,255,255,0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  z-index: 10;
  position: relative;
}

.header-title h2 {
  margin: 0;
  font-size: 1.5rem;
  background: linear-gradient(135deg, #364050 0%, #364050 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-title p {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #64748b;
}

.header-filters {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.date-filter label,
.location-filter label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.calendar-picker-container {
  position: relative;
  display: flex;
  align-items: center;
}

.calendar-input {
  cursor: pointer;
  background-color: white;
  padding-right: 35px !important;
}

.calendar-input.calendar-active {
  border-color: #3640504b;
  box-shadow: 0 0 0 2px rgba(54, 64, 80, 0.1);
}

.calendar-icon {
  position: absolute;
  right: 12px;
  cursor: pointer;
  color: #64748b;
  font-size: 16px;
  transition: all 0.2s ease;
  pointer-events: auto;
}

.calendar-icon:hover {
  color: #364050;
  transform: scale(1.1);
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  width: 180px;
}

.location-select {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
}

.refresh-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #364050 0%, #364050 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  color: white;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102,126,234,0.4);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-sm {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Content Layout */
.operations-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 1px;
  background: rgba(0,0,0,0.05);
  position: relative;
}

/* Trips Sidebar */
.trips-sidebar {
  width: 520px;
  background: linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98));
  display: flex;
  flex-direction: column;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  z-index: 5;
  box-shadow: 4px 0 20px rgba(0,0,0,0.05);
}

.trips-sidebar.collapsed {
  width: 48px;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(102,126,234,0.1);
  background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.95));
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  background: linear-gradient(135deg, #364050, #364050);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.collapse-btn {
  background: rgba(102,126,234,0.1);
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 6px 10px;
  border-radius: 8px;
  transition: all 0.2s;
  color: #364050;
}

.collapse-btn:hover {
  background: rgba(102,126,234,0.2);
  transform: scale(1.05);
}

.trips-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.trips-list::-webkit-scrollbar {
  display: none;
}

.loading-trips,
.no-trips {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #364050;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin: 0 auto 12px;
}

.trip-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Trip Cards */
.trip-card {
  background: white;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.trip-gradient-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #364050, #364050, #364050, #364050);
  opacity: 0;
  transition: opacity 0.3s;
}

.trip-card:hover .trip-gradient-bg {
  opacity: 1;
}

.trip-card.active .trip-id strong,
.trip-card.active .trip-badge,
.trip-card.active .detail-label,
.trip-card.active .detail-value,
.trip-card.active .detail-icon,
.trip-card.active .progress-label,
.trip-card.active .progress-label span,
.trip-card.active .detail-value.highlight,
.trip-card.active .truck-name {
  color: white !important;
}

.trip-card.active .trip-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white !important;
}

.trip-card.active .progress-track {
  background: rgba(255, 255, 255, 0.3);
}

.trip-card.active .progress-fill {
  background: linear-gradient(90deg, #ffffff, #ffffff);
}

.trip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 12px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.trip-id {
  display: flex;
  align-items: center;
  gap: 10px;
}

.trip-icon {
  font-size: 1.2rem;
}

.trip-id strong {
  font-size: 1rem;
  color: #1e293b;
}

.trip-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
}

.badge-active {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}

.badge-completed {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  color: #166534;
}

.badge-cancelled {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #991b1b;
}

.trip-details {
  padding: 12px 16px;
}

.detail-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.detail-row.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.detail-item {
  display: flex;
  gap: 8px;
}

.detail-icon {
  font-size: 1rem;
  opacity: 0.7;
}

.detail-info {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.detail-info2 {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 300;
  color: #535a63;
  text-transform: uppercase;
}

.detail-value {
  font-size: 0.75rem;
  font-weight: 300;
  color: #334155;
}

.detail-value.highlight {
  color: #364050;
  font-size: 0.9rem;
}

.truck-name {
  color: #8b5cf6;
  font-weight: 700;
}

/* Map Container */
.map-container {
  flex: 1;
  position: relative;
  background: #e2e8f0;
  overflow: hidden;
}

.delivery-map {
  width: 100%;
  height: 100%;
  z-index: 1;
  transition: transform 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

.delivery-map.map-zooming {
  transform: scale(1.01);
}

.map-loading,
.map-error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.9);
  z-index: 10;
}

.retry-btn {
  margin-top: 12px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #364050 0%, #364050 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* Map Legend */
.map-legend {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 12px 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 100;
  font-size: 0.75rem;
  min-width: 150px;
}

.legend-title {
  font-weight: 700;
  margin-bottom: 10px;
  color: #1e293b;
  background: linear-gradient(135deg, #364050, #364050);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  color: #475569;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.legend-line {
  width: 24px;
  height: 3px;
  display: inline-block;
  border-radius: 2px;
}

.legend-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
  margin: 10px 0;
}

/* Order Details Panel */
.order-details-panel {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 340px;
  background: rgba(255,255,255,0.98);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.2);
  z-index: 100;
  overflow: hidden;
}

.panel-slide-enter-active {
  animation: slideInRight 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.panel-slide-leave-active {
  animation: slideOutRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes slideOutRight {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 1px solid #e2e8f0;
}

.panel-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  background: linear-gradient(135deg, #364050, #364050);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.close-panel {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #94a3b8;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-panel:hover {
  background: rgba(0,0,0,0.05);
  color: #1e293b;
}

.panel-content {
  padding: 16px 20px;
  max-height: 450px;
  overflow-y: auto;
}

.detail-group {
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}

.detail-group:last-child {
  border-bottom: none;
}

.detail-group label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.detail-group span {
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 500;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-delivered {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  color: #166534;
}

.status-progress {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
}

.status-pending {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}

.status-scheduled {
  background: linear-gradient(135deg, #f3e8ff, #e9d5ff);
  color: #6b21a5;
}

.status-cancelled {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #991b1b;
}

/* Calendar Modal Styles */
.calendar-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    backdrop-filter: blur(0);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(8px);
  }
}

.calendar-modal-content {
  background: white;
  width: 95vw;
  max-width: 1200px;
  height: 90vh;
  max-height: 800px;
  border-radius: 24px;
  animation: modalSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.calendar-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  flex-shrink: 0;
}

.calendar-modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.calendar-modal-body {
  flex: 1;
  background: #f1f5f9;
  overflow-y: auto;
  padding: 20px;
}

.calendar-modal-footer {
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

.cancel-btn {
  padding: 8px 24px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.today-btn {
  padding: 8px 24px;
  background: #008ed5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: white;
  transition: all 0.2s;
}

.today-btn:hover {
  background: #0369a1;
  transform: translateY(-1px);
}

/* Calendar Navigation */
.calendar-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.nav-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  color: #475569;
}

.nav-btn:hover {
  background: #e2e8f0;
  transform: scale(1.02);
}

.current-month {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  text-align: center;
}

.calendar-location-badge {
  padding: 6px 12px;
  background: #e0f2fe;
  border-radius: 20px;
  font-size: 12px;
  color: #0369a1;
  font-weight: 500;
}

.calendar-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 40px;
  color: #64748b;
}

/* Calendar Grid */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.calendar-weekday {
  padding: 14px 8px;
  text-align: center;
  font-weight: 700;
  font-size: 12px;
  color: #64748b;
  background: #fafcff;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #edf2f7;
}

.calendar-weekday:last-child {
  border-right: none;
}

/* Updated Calendar Day Styles - Larger Fonts */
.calendar-day {
  min-height: 130px;
  padding: 12px;
  border-right: 1px solid #edf2f7;
  border-bottom: 1px solid #edf2f7;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.calendar-day:hover {
  background: #f8fafc;
  transform: scale(0.98);
}

.day-number {
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 10px;
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
}

.calendar-day.today .day-number {
  background: #eab308;
  color: white;
}

.day-stats {
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.stat-row {
  padding: 3px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 12px;
}

.stat-row:first-child {
  font-weight: 600;
  color: #2563eb;
}

.stat-row:nth-child(2) {
  color: #059669;
}

.stat-row:nth-child(3) {
  color: #d97706;
}

.stat-row:nth-child(4) {
  color: #dc2626;
}

.no-deliveries {
  color: #cbd5e1;
  font-size: 22px;
  text-align: center;
  margin-top: 28px;
  font-weight: 400;
}

/* Make calendar grid rows taller for more content */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.calendar-weekday {
  padding: 14px 8px;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  color: #64748b;
  background: #fafcff;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #edf2f7;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .calendar-day {
    min-height: 100px;
    padding: 8px;
  }
  
  .day-number {
    font-size: 14px;
    margin-bottom: 6px;
  }
  
  .day-stats {
    font-size: 10px;
    gap: 3px;
  }
  
  .stat-row {
    font-size: 9px;
    padding: 2px 0;
  }
  
  .no-deliveries {
    font-size: 16px;
    margin-top: 18px;
  }
  
  .calendar-weekday {
    font-size: 10px;
    padding: 10px 4px;
  }
}

.calendar-day.other-month {
  background: #fafafa;
}

.calendar-day.other-month .day-number {
  color: #cbd5e1;
}

.calendar-day.today {
  background: #fefce8;
  position: relative;
}

.calendar-day.today .day-number {
  background: #eab308;
  color: white;
  padding: 2px 8px;
  border-radius: 20px;
  display: inline-block;
}

.calendar-day.has-trips {
  background: linear-gradient(135deg, #ffffff, #f0f9ff);
}

.calendar-day.selected {
  background: #008ed520;
  border: 2px solid #008ed5;
  transform: scale(0.98);
}

.day-number {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 8px;
  display: inline-block;
}

.day-trip-stats {
  font-size: 11px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
}

.stat-trips {
  color: #2563eb;
  font-weight: 600;
  font-size: 11px;
}

.stat-deliveries {
  color: #059669;
  font-weight: 500;
  font-size: 10px;
}

.day-no-trips {
  text-align: center;
  margin-top: 20px;
}

.no-trips-text {
  color: #cbd5e1;
  font-size: 18px;
}

/* Loading Overlay Styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay-enter-active {
  animation: fadeIn 0.3s ease;
}

.loading-overlay-leave-active {
  animation: fadeOut 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  max-width: 450px;
  width: 90%;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 32px;
}

.loading-ring {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 32px;
  font-weight: bold;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

.loading-logo i {
  font-size: 40px;
  background: linear-gradient(135deg, #4299e1, #48bb78);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 24px;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.3rem;
  margin-bottom: 8px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
}

.loading-progress {
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 20px;
}

.tip-text {
  color: #718096;
  font-size: 0.9rem;
  animation: tipFade 0.5s ease;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Animations */
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Leaflet Popup Customization */
:deep(.order-popup) {
  min-width: 220px;
  border-radius: 12px;
  overflow: hidden;
}

:deep(.popup-header) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

:deep(.popup-status) {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
}

:deep(.view-order-btn) {
  width: 100%;
  padding: 8px;
  background: linear-gradient(135deg, #364050 0%, #364050 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

:deep(.view-order-btn:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102,126,234,0.4);
}

/* Responsive */
@media (max-width: 768px) {
  .trips-sidebar {
    width: 320px;
  }
  
  .order-details-panel {
    width: 300px;
    bottom: 10px;
    right: 10px;
  }
  
  .map-legend {
    bottom: 10px;
    left: 10px;
    font-size: 0.7rem;
    padding: 8px 12px;
  }
  
  .header-filters {
    flex-wrap: wrap;
    width: 100%;
  }

  .loading-card {
    padding: 32px 24px;
  }

  .date-input {
    width: 100%;
  }
  
  .calendar-picker-container {
    width: 100%;
  }
  
  .calendar-modal-content {
    width: 98vw;
    height: 95vh;
    border-radius: 16px;
  }
  
  .calendar-modal-header {
    padding: 16px 20px;
  }
  
  .calendar-modal-header h3 {
    font-size: 1.2rem;
  }
  
  .modal-close-btn {
    width: 32px;
    height: 32px;
    font-size: 24px;
  }
  
  .calendar-modal-body {
    padding: 12px;
  }
  
  .calendar-day {
    min-height: 80px;
    padding: 6px;
  }
  
  .day-trip-stats {
    font-size: 9px;
  }
  
  .stat-trips, .stat-deliveries {
    font-size: 9px;
  }
  
  .calendar-nav {
    padding: 12px;
  }
  
  .nav-btn {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  .current-month {
    font-size: 1rem;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsDeliveryOperations.vue